import { useState } from 'react'
import { format, isValid, parse } from 'date-fns'
import { CalendarIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

interface Props {
    id: string
    /** yyyy-MM-ddTHH:mm (what a datetime-local input produced, so stored answers keep working). */
    value: string
    onChange: (value: string) => void
    invalid?: boolean
}

const FORMAT = "yyyy-MM-dd'T'HH:mm"

/** Date picker (calendar popover) plus a time field, saved as one local date-time. */
export default function DateTimeInput({ id, value, onChange, invalid }: Props) {
    const [open, setOpen] = useState(false)
    const parsed = value ? parse(value, FORMAT, new Date()) : undefined
    const selected = parsed && isValid(parsed) ? parsed : undefined
    const time = selected ? format(selected, 'HH:mm') : ''

    const setDay = (d: Date | undefined) => {
        if (!d) return onChange('')
        onChange(`${format(d, 'yyyy-MM-dd')}T${time || '09:00'}`)
        setOpen(false)
    }
    const setTime = (t: string) => {
        if (!selected) return // pick a day first
        onChange(`${format(selected, 'yyyy-MM-dd')}T${t || '00:00'}`)
    }

    return (
        <div className="flex flex-wrap items-center gap-2">
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button id={id} variant="outline" aria-invalid={invalid || undefined}
                        className={cn('w-56 justify-start gap-2 font-normal', !selected && 'text-muted-foreground')}>
                        <CalendarIcon className="size-4" />
                        {selected ? format(selected, 'PPP') : 'Pick a date'}
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={selected} defaultMonth={selected} onSelect={setDay} autoFocus />
                </PopoverContent>
            </Popover>
            <Input type="time" aria-label="Time" className="w-32 appearance-none [&::-webkit-calendar-picker-indicator]:hidden"
                value={time} disabled={!selected} aria-invalid={invalid || undefined} onChange={(e) => setTime(e.target.value)} />
        </div>
    )
}
